import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../AuthProvider";
import { db, storage } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { getAuthErrorMessage } from "../utils/authErrors";

const inputClassName =
  "w-full rounded-2xl border border-[#ead7b0] bg-white/95 px-4 py-3 text-[#4e3520] shadow-[0_12px_30px_-28px_rgba(69,39,15,0.45)] outline-none transition placeholder:text-[#b39368] focus:border-[#c99649] focus:ring-2 focus:ring-[#efd8aa]";

const primaryButtonClassName =
  "rounded-2xl bg-[linear-gradient(135deg,#a56a22,#d6a654)] px-5 py-3 text-sm font-semibold text-white shadow-[0_20px_40px_-24px_rgba(118,73,20,0.65)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60";

const secondaryButtonClassName =
  "rounded-2xl border border-[#ead7b0] bg-white px-4 py-2 text-sm font-semibold text-[#7d5a2a] transition hover:bg-[#fff7e8]";

function ImageCard({
  title,
  src,
  alt,
  onChoose,
  shape = "card",
  emptyLabel = "No image uploaded yet",
}) {
  const imageClassName =
    shape === "avatar"
      ? "h-24 w-24 rounded-full border border-[#ead7b0] object-cover shadow-sm"
      : "h-36 w-full rounded-[24px] border border-[#ead7b0] object-cover shadow-sm";

  return (
    <div className="rounded-[28px] border border-[#f0dfc0] bg-[#fffdfa] p-4 shadow-[0_20px_40px_-30px_rgba(123,78,27,0.35)]">
      <div className="text-sm font-semibold text-[#6c4d2c]">{title}</div>
      <div className="mt-4 flex flex-col items-center gap-4 text-center">
        {src ? (
          <img src={src} alt={alt} className={imageClassName} />
        ) : (
          <div
            className={`${imageClassName} flex items-center justify-center border-dashed bg-[#fff7e8] px-4 text-xs font-medium text-[#b39368]`}
          >
            {emptyLabel}
          </div>
        )}
        <button type="button" onClick={onChoose} className={secondaryButtonClassName}>
          {src ? "Change" : "Upload"}
        </button>
      </div>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur">
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/70">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-white">{value}</div>
    </div>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <section className="rounded-[32px] border border-[#f0dfc0] bg-white/95 p-6 shadow-[0_24px_60px_-40px_rgba(113,72,24,0.35)]">
      <div className="mb-5">
        <div className="text-xl font-bold text-[#4e3520]">{title}</div>
        {subtitle ? (
          <div className="mt-1 text-sm text-[#8d724c]">{subtitle}</div>
        ) : null}
      </div>
      {children}
    </section>
  );
}

const Profile = () => {
  const { user, userData, refreshUserData } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [edit, setEdit] = useState({
    fullName: "",
    phone: "",
    whatsapp: "",
    nic: "",
    bankName: "",
    bankAccount: "",
    bankBranch: "",
    accountHolder: "",
    profilePicUrl: "",
    nicFrontUrl: "",
    nicBackUrl: "",
    bankFrontUrl: "",
  });
  const [file, setFile] = useState({
    profilePic: null,
    nicFront: null,
    nicBack: null,
    bankFront: null,
  });
  const [updating, setUpdating] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [pwFields, setPwFields] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState("");
  const fileInput = useRef({});

  useEffect(() => {
    if (!user?.uid) return;

    const load = async () => {
      setLoadingProfile(true);
      try {
        const docSnap = await getDoc(doc(db, "referral_users", user.uid));
        if (!docSnap.exists()) {
          setProfileData(userData || null);
          return;
        }

        const data = docSnap.data();
        setProfileData(data);
        setEdit({
          fullName: data.fullName || "",
          phone: data.phone || "",
          whatsapp: data.whatsapp || "",
          nic: data.nic || "",
          bankName: data.bankName || "",
          bankAccount: data.bankAccount || "",
          bankBranch: data.bankBranch || "",
          accountHolder: data.accountHolder || "",
          profilePicUrl: data.profilePicUrl || data.imageUrl || "",
          nicFrontUrl: data.nicFrontUrl || "",
          nicBackUrl: data.nicBackUrl || "",
          bankFrontUrl: data.bankFrontUrl || "",
        });
      } finally {
        setLoadingProfile(false);
      }
    };

    load();
  }, [user?.uid, userData]);

  const profileCompletion = useMemo(() => {
    const checks = [
      edit.fullName,
      edit.phone,
      edit.whatsapp,
      edit.nic,
      edit.bankName,
      edit.bankAccount,
      edit.bankBranch,
      edit.accountHolder,
      edit.profilePicUrl,
      edit.nicFrontUrl,
      edit.nicBackUrl,
      edit.bankFrontUrl,
    ];
    const completed = checks.filter(Boolean).length;
    return Math.round((completed / checks.length) * 100);
  }, [
    edit.accountHolder,
    edit.bankAccount,
    edit.bankBranch,
    edit.bankFrontUrl,
    edit.bankName,
    edit.fullName,
    edit.nic,
    edit.nicBackUrl,
    edit.nicFrontUrl,
    edit.phone,
    edit.profilePicUrl,
    edit.whatsapp,
  ]);

  const statusTone =
    profileData?.status === "approved"
      ? "bg-[#edf8ea] text-[#41733b] border-[#cfe2c9]"
      : "bg-[#fff6e8] text-[#8a5a1d] border-[#f0dfc0]";

  const handleFileChange = (e, type) => {
    const fileObj = e.target.files?.[0];
    if (!fileObj) return;

    setFile((prev) => ({ ...prev, [type]: fileObj }));
    const url = URL.createObjectURL(fileObj);
    setEdit((prev) => ({ ...prev, [`${type}Url`]: url }));
  };

  const openPicker = (key) => fileInput.current[key]?.click();

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setUpdating(true);

    try {
      const urls = {};

      if (file.profilePic) {
        const storageRef = ref(storage, `profile_images/${user.uid}`);
        await uploadBytes(storageRef, file.profilePic);
        const profileUrl = await getDownloadURL(storageRef);
        urls.profilePicUrl = profileUrl;
        urls.imageUrl = profileUrl;
      }
      if (file.nicFront) {
        const storageRef = ref(storage, `nic_front_images/${user.uid}`);
        await uploadBytes(storageRef, file.nicFront);
        urls.nicFrontUrl = await getDownloadURL(storageRef);
      }
      if (file.nicBack) {
        const storageRef = ref(storage, `nic_back_images/${user.uid}`);
        await uploadBytes(storageRef, file.nicBack);
        urls.nicBackUrl = await getDownloadURL(storageRef);
      }
      if (file.bankFront) {
        const storageRef = ref(storage, `bank_front_images/${user.uid}`);
        await uploadBytes(storageRef, file.bankFront);
        urls.bankFrontUrl = await getDownloadURL(storageRef);
      }

      await updateDoc(doc(db, "referral_users", user.uid), {
        ...edit,
        ...urls,
        status: "pending",
      });

      const nextProfileData = {
        ...(profileData || {}),
        ...edit,
        ...urls,
        status: "pending",
      };

      setProfileData(nextProfileData);
      setEdit((prev) => ({ ...prev, ...urls }));
      setFile({
        profilePic: null,
        nicFront: null,
        nicBack: null,
        bankFront: null,
      });
      setSuccess("Profile updated. Your details are now pending admin review.");
      refreshUserData?.();
    } catch (e) {
      setError("Failed to update your profile. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwMsg("");

    if (!pwFields.current || !pwFields.new || !pwFields.confirm) {
      setPwMsg("All password fields are required.");
      return;
    }
    if (pwFields.new !== pwFields.confirm) {
      setPwMsg("Passwords do not match.");
      return;
    }

    setPwLoading(true);
    try {
      const cred = EmailAuthProvider.credential(user.email, pwFields.current);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, pwFields.new);
      setPwFields({ current: "", new: "", confirm: "" });
      setPwMsg("Password updated successfully!");
    } catch (e) {
      setPwMsg(getAuthErrorMessage(e, "passwordChange"));
    } finally {
      setPwLoading(false);
    }
  };

  if (loadingProfile) {
    return <div className="p-8 text-center text-[#6c4d2c]">Loading profile...</div>;
  }

  if (!profileData) {
    return <div className="p-8 text-center text-[#6c4d2c]">Profile not found.</div>;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#fff4dd,_#f5f7ff_42%,_#eef3ff_100%)] px-4 py-6 md:px-6 md:py-10">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="grid gap-6 lg:grid-cols-[340px,1fr]">
          <aside className="overflow-hidden rounded-[36px] bg-[linear-gradient(160deg,#1d1739,#2c2258_52%,#6f4a1e_120%)] p-6 text-white shadow-[0_30px_80px_-40px_rgba(20,14,43,0.8)]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
                  Affiliate Portal
                </div>
                <h1 className="mt-4 text-3xl font-black leading-tight">
                  Profile settings
                </h1>
                <p className="mt-3 text-sm leading-6 text-white/70">
                  Keep your referral identity, payout details, and optional
                  verification files neatly organized.
                </p>
              </div>
              <img
                src={edit.profilePicUrl || "/user-portal-logo.png"}
                alt="Affiliate profile"
                className="h-16 w-16 rounded-3xl border border-white/15 object-cover"
              />
            </div>

            <div className="mt-8 rounded-[30px] border border-white/10 bg-white/10 p-5 backdrop-blur">
              <div className="flex items-center gap-4">
                <img
                  src={edit.profilePicUrl || "/avatar.png"}
                  alt="Profile"
                  className="h-20 w-20 rounded-full border-4 border-white/15 object-cover shadow-lg"
                />
                <div className="min-w-0">
                  <div className="truncate text-xl font-bold">
                    {edit.fullName || "Affiliate member"}
                  </div>
                  <div className="truncate text-sm text-white/70">
                    {user.email}
                  </div>
                </div>
              </div>

              <div
                className={`mt-4 inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusTone}`}
              >
                {profileData?.status === "approved"
                  ? "Approved account"
                  : `Status: ${profileData?.status || "pending"}`}
              </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              <StatPill label="Profile Ready" value={`${profileCompletion}%`} />
              <StatPill
                label="Identity Docs"
                value={
                  edit.nicFrontUrl || edit.nicBackUrl ? "Added" : "Optional"
                }
              />
              <StatPill
                label="Payout Setup"
                value={edit.bankAccount ? "Configured" : "Pending"}
              />
            </div>
          </aside>

          <div className="space-y-6">
            <SectionCard
              title="Account overview"
              subtitle="Update the contact and payout details your admin team uses to manage your affiliate account."
            >
              {(success || error) && (
                <div
                  className={`mb-5 rounded-2xl border px-4 py-3 text-sm ${
                    error
                      ? "border-[#e7b0ab] bg-[#fff4f3] text-[#9f3b2f]"
                      : "border-[#c8ddb3] bg-[#f4faee] text-[#527239]"
                  }`}
                >
                  {error || success}
                </div>
              )}

              <form onSubmit={handleSaveProfile} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                      Full Name
                    </label>
                    <input
                      type="text"
                      className={inputClassName}
                      value={edit.fullName}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, fullName: e.target.value }))
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                      Mobile Number
                    </label>
                    <input
                      type="text"
                      className={inputClassName}
                      value={edit.phone}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, phone: e.target.value }))
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                      WhatsApp Number
                    </label>
                    <input
                      type="text"
                      className={inputClassName}
                      value={edit.whatsapp}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, whatsapp: e.target.value }))
                      }
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                      NIC Number
                    </label>
                    <input
                      type="text"
                      className={inputClassName}
                      value={edit.nic}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, nic: e.target.value }))
                      }
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="rounded-[28px] border border-[#f0dfc0] bg-[#fff9ef] p-4 text-sm leading-6 text-[#7d5a2a]">
                  Identity details are optional. You can upload NIC or bank
                  proof later whenever you need to complete verification.
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      className={inputClassName}
                      value={edit.bankName}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, bankName: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                      Account Holder
                    </label>
                    <input
                      type="text"
                      className={inputClassName}
                      value={edit.accountHolder}
                      onChange={(e) =>
                        setEdit((prev) => ({
                          ...prev,
                          accountHolder: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                      Bank Account Number
                    </label>
                    <input
                      type="text"
                      className={inputClassName}
                      value={edit.bankAccount}
                      onChange={(e) =>
                        setEdit((prev) => ({
                          ...prev,
                          bankAccount: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                      Bank Branch
                    </label>
                    <input
                      type="text"
                      className={inputClassName}
                      value={edit.bankBranch}
                      onChange={(e) =>
                        setEdit((prev) => ({
                          ...prev,
                          bankBranch: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <ImageCard
                    title="Profile Picture"
                    src={edit.profilePicUrl}
                    alt="Profile"
                    shape="avatar"
                    emptyLabel="Upload your profile photo"
                    onChoose={() => openPicker("profilePic")}
                  />
                  <ImageCard
                    title="NIC Front"
                    src={edit.nicFrontUrl}
                    alt="NIC front"
                    emptyLabel="Optional NIC front image"
                    onChoose={() => openPicker("nicFront")}
                  />
                  <ImageCard
                    title="NIC Back"
                    src={edit.nicBackUrl}
                    alt="NIC back"
                    emptyLabel="Optional NIC back image"
                    onChoose={() => openPicker("nicBack")}
                  />
                  <ImageCard
                    title="Bank Proof"
                    src={edit.bankFrontUrl}
                    alt="Bank proof"
                    emptyLabel="Optional bank document"
                    onChoose={() => openPicker("bankFront")}
                  />
                </div>

                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={(el) => (fileInput.current.profilePic = el)}
                  onChange={(e) => handleFileChange(e, "profilePic")}
                />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={(el) => (fileInput.current.nicFront = el)}
                  onChange={(e) => handleFileChange(e, "nicFront")}
                />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={(el) => (fileInput.current.nicBack = el)}
                  onChange={(e) => handleFileChange(e, "nicBack")}
                />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={(el) => (fileInput.current.bankFront = el)}
                  onChange={(e) => handleFileChange(e, "bankFront")}
                />

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    className={primaryButtonClassName}
                    disabled={updating}
                  >
                    {updating ? "Saving..." : "Save Profile"}
                  </button>
                  <div className="text-sm text-[#8d724c]">
                    Saving profile changes will set this account back to pending
                    review.
                  </div>
                </div>
              </form>
            </SectionCard>

            <SectionCard
              title="Security"
              subtitle="Change your password whenever you need. This does not affect your profile approval status."
            >
              <form onSubmit={handlePasswordChange} className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                    Current Password
                  </label>
                  <input
                    type="password"
                    className={inputClassName}
                    value={pwFields.current}
                    onChange={(e) =>
                      setPwFields((prev) => ({ ...prev, current: e.target.value }))
                    }
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                    New Password
                  </label>
                  <input
                    type="password"
                    className={inputClassName}
                    value={pwFields.new}
                    onChange={(e) =>
                      setPwFields((prev) => ({ ...prev, new: e.target.value }))
                    }
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    className={inputClassName}
                    value={pwFields.confirm}
                    onChange={(e) =>
                      setPwFields((prev) => ({ ...prev, confirm: e.target.value }))
                    }
                    required
                  />
                </div>
                <div className="md:col-span-2 flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    className={primaryButtonClassName}
                    disabled={pwLoading}
                  >
                    {pwLoading ? "Updating..." : "Change Password"}
                  </button>
                  {pwMsg ? (
                    <div
                      className={`text-sm font-semibold ${
                        pwMsg.includes("success")
                          ? "text-[#527239]"
                          : "text-[#9f3b2f]"
                      }`}
                    >
                      {pwMsg}
                    </div>
                  ) : null}
                </div>
              </form>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
